/**
 * @id          polyFightProps
 * Polymarket FIGHT props (UFC/boxing) — the per-fight Yes/No and O/U markets
 * Gamma lists under a fight's event, on the fight's own slug stem:
 *
 *   PM-ufc-bre1-chr20-2026-10-10-go-the-distance
 *   PM-ufc-bre1-chr20-2026-10-10-win-by-ko-tko | -allen-win-by-ko-tko
 *   PM-ufc-bre1-chr20-2026-10-10-win-by-submission | -win-by-decision
 *   PM-ufc-bre1-chr20-2026-10-10-totals-2pt5            (O/U rounds, YES = Over)
 *   PM-ufc-bre1-chr20-2026-10-10-ends-before-round-3
 *   PM-ufc-bre1-chr20-2026-10-10-allen-win-in-round-1
 *   PM-ufc-bre1-chr20-2026-10-10-decision-or-draw
 *
 * Hydracore admits exactly this grammar (poly-ufc-identity, 2026-10-06) and
 * ships the rows game-family with the fight's canonical key so the edge
 * streams live books. The store's Poly ticker parser fails closed on every
 * suffixed fight slug, so these rows never enter the game-line selectors; the
 * Fight Props tab reads them through `classifyPolyFightProp` by STEM — the
 * fight's own moneyline ticker — which also serves a Poly-only fight Kalshi
 * does not list. Labels come from the row's `title` (the Gamma question);
 * this module only groups and orders.
 */

export type FightPropGroup = 'distance' | 'method' | 'rounds' | 'round';

export interface PolyFightProp {
  /** The fight's moneyline ticker (`PM-ufc-a-b-YYYY-MM-DD`). */
  stem: string;
  group: FightPropGroup;
  /** Rounds O/U line (`-totals-2pt5` → 2.5); absent elsewhere. */
  line?: number;
  /** Round number for `-ends-before-round-N` / `-win-in-round-N`. */
  round?: number;
  /** Gamma's fighter slug on a fighter-scoped prop (`allen`). */
  fighter?: string;
  /** Stable sort key inside the fight: group order, then line/round/fighter. */
  order: number;
}

const STEM_RE = /^(PM-(?:ufc|boxing)-[a-z0-9]+-[a-z0-9]+-\d{4}-\d{2}-\d{2})(-.+)$/;
const FIGHTER = '[a-z]+(?:-[a-z]+)*';
const METHOD_RE = new RegExp(`^-(?:(${FIGHTER})-)?win-by-(ko-tko|submission|decision)$`);
const WIN_IN_ROUND_RE = new RegExp(`^-(${FIGHTER})-win-in-round-(\\d)$`);

const GROUP_ORDER: Record<FightPropGroup, number> = { distance: 0, method: 1, rounds: 2, round: 3 };
const METHOD_ORDER: Record<string, number> = { 'ko-tko': 0, submission: 1, decision: 2 };

export const FIGHT_PROP_GROUP_LABEL: Record<FightPropGroup, string> = {
  distance: 'Distance',
  method: 'Method of Victory',
  rounds: 'Total Rounds',
  round: 'Round',
};

export function classifyPolyFightProp(ticker: string): PolyFightProp | null {
  const m = STEM_RE.exec(ticker);
  if (!m) return null;
  const stem = m[1];
  const suffix = m[2];
  const base = (group: FightPropGroup) => GROUP_ORDER[group] * 1000;

  if (suffix === '-go-the-distance') return { stem, group: 'distance', order: base('distance') };

  const method = METHOD_RE.exec(suffix);
  if (method) {
    const fighter = method[1];
    // Fight-level first, then per fighter; KO/TKO before submission before decision.
    const order = base('method') + (fighter ? 100 : 0) + (METHOD_ORDER[method[2]] ?? 9);
    return { stem, group: 'method', order, ...(fighter ? { fighter } : {}) };
  }

  const totals = /^-totals-(\d+)pt(\d+)$/.exec(suffix);
  if (totals) {
    const line = Number(`${totals[1]}.${totals[2]}`);
    return { stem, group: 'rounds', line, order: base('rounds') + line * 10 };
  }

  const ends = /^-ends-before-round-(\d)$/.exec(suffix);
  if (ends) {
    const round = Number(ends[1]);
    return { stem, group: 'round', round, order: base('round') + round };
  }

  const win = WIN_IN_ROUND_RE.exec(suffix);
  if (win) {
    const round = Number(win[2]);
    return { stem, group: 'round', round, fighter: win[1], order: base('round') + 100 + round * 10 };
  }

  if (suffix === '-decision-or-draw') return { stem, group: 'round', order: base('round') + 900 };

  return null;
}

/** The fight stem a prop ticker belongs to, or null for anything else. */
export function fightPropStem(ticker: string): string | null {
  return classifyPolyFightProp(ticker)?.stem ?? null;
}
